import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Card } from '@altermaind/web/ui';
import { PaginatedTable, usePaginatedTable } from '@/_shared/components/Table';
import { Column } from '@/_shared/components/Table/types';
import { TableHeader } from '@/_shared/components/TableHeader';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { getReportsTrainingsCall } from '../../state/api/getReportsTrainingsCall';
import { PAGE_SIZE_REPORTS } from '../../state/constants/pagination';
import { TrainingEmployee } from '../../state/types';

const MINUTES_IN_HOUR = 60;

export const TableTrainings: FC = () => {
  const t = useTranslations('reports.trainings.table');

  const {
    data,
    totalItems,
    loading: loadingGetReportsTrainings,
    execute: executeGetReportsTrainings,
    reset: resetGetReportsTrainings,
  } = getReportsTrainingsCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_REPORTS,
    totalItems: totalItems || 0,
  });

  const [querySearch, setQuerySearch] = useState<string>('');

  const columns: Column[] = useMemo(() => {
    const formationTimeFormat = (trainingTime: number | undefined) => {
      if (trainingTime === undefined) return '-';
      const hours = Math.floor(trainingTime / MINUTES_IN_HOUR);
      const minutes = trainingTime % MINUTES_IN_HOUR;
      return `${hours}h ${minutes}min`;
    };

    return [
      {
        key: 'firstName',
        sortable: true,
        title: t('firstName'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.firstName || '-'}</ValueInTable>
        ),
      },
      {
        key: 'lastName',
        sortable: true,
        title: t('lastName'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.lastName || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyRole',
        sortable: true,
        title: t('role'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.companyRole || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyDepartment',
        sortable: true,
        title: t('department'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.companyDepartment || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyArea',
        sortable: true,
        title: t('team'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.companyArea || '-'}</ValueInTable>
        ),
      },
      {
        key: 'trainingTimeInMinutes',
        sortable: true,
        title: t('trainingTime'),
        titleTooltip: t('trainingTimeTooltip'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{formationTimeFormat(item.trainingTimeInMinutes)}</ValueInTable>
        ),
      },
      {
        key: 'completedContents',
        sortable: true,
        title: t('completedContents'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.completedContents ?? '-'}</ValueInTable>
        ),
      },
      {
        key: 'contentsInProgress',
        sortable: true,
        title: t('contentsInProgress'),
        renderValue: (item: TrainingEmployee) => (
          <ValueInTable>{item.contentsInProgress ?? '-'}</ValueInTable>
        ),
      },
    ];
  }, [t]);

  const onSearchCallback = useCallback((value: string) => {
    setQuerySearch(value);
  }, []);

  useEffect(() => {
    executeGetReportsTrainings({
      length: PAGE_SIZE_REPORTS,
      start: 0,
      q: querySearch || '',
    });
    return () => {
      resetGetReportsTrainings();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySearch, getReportsTrainingsCall]);

  return (
    <Card mt='s' mb='s'>
      <TableHeader
        onSearchCallback={onSearchCallback}
        data-testid='reports-trainings-hard-skills-button'
      />
      <PaginatedTable
        loading={loadingGetReportsTrainings}
        columns={columns}
        pageSize={PAGE_SIZE_REPORTS}
        call={{ ...getReportsTrainingsCall }}
        paginedProps={paginedProps}
        onRowClick={() => {
          /* empty */
        }}
      />
    </Card>
  );
};
